import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanSearch, Waves, Waypoints, Moon, Check, MessageCircle, ArrowRight, ShieldAlert, CalendarDays } from "lucide-react";
import { Reveal, FadeIn } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { BookingTrigger } from "../components/BookingDialog";
import { useLang } from "../LanguageContext";
import { IMAGES, waLink } from "../data/site";

const ICONS = [ScanSearch, Waves, Waypoints, Moon];

const OfferCard = ({ offer, index }) => {
  const Icon = ICONS[index];
  return (
    <motion.article
      data-testid={`offer-card-${index}`}
      className="offer-card lg:col-span-6"
      initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: 0.08 * index, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-gold/[0.07] blur-3xl" />
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-gold/25 bg-gold/10">
        <Icon className="h-5 w-5 text-gold" />
      </div>
      <p className="eyebrow mt-6">{offer.tagline}</p>
      <h3 className="mt-3 font-serif text-2xl font-light leading-snug text-white sm:text-3xl">{offer.title}</h3>
      <p className="mt-4 text-sm leading-relaxed text-white/60 sm:text-base">{offer.description}</p>
      <ul className="mt-6 space-y-2.5">
        {offer.points.map((p) => (
          <li key={p} className="flex items-start gap-3 text-sm text-white/60">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" /> {p}
          </li>
        ))}
      </ul>
      <div className="mt-7 flex flex-wrap items-baseline gap-x-5 gap-y-1">
        <p className="font-mono text-2xl tracking-wide text-gold-light" data-testid={`offer-price-${index}`}>{offer.price}</p>
        {offer.duration && (
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-mint">{offer.duration}</p>
        )}
      </div>
      <p className="mt-1.5 font-mono text-xs text-white/50" data-testid={`offer-price-alt-${index}`}>{offer.priceAlt}</p>
    </motion.article>
  );
};

const Accompagnements = () => {
  const { s } = useLang();
  return (
    <main data-testid="accompagnements-page">
      <section className="relative overflow-hidden pb-10 pt-40">
        <img src={IMAGES.veil} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/85 to-obsidian" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
          <FadeIn><p className="eyebrow">{s.acc.eyebrow}</p></FadeIn>
          <h1 className="mt-6 font-serif text-5xl font-light leading-[1.02] text-white sm:text-7xl">
            <span className="block">{s.acc.titleA}</span>
            <span className="block italic text-halo">{s.acc.titleB}</span>
          </h1>
          <FadeIn delay={0.5}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/65 sm:text-lg">{s.acc.paragraph}</p>
          </FadeIn>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto w-full max-w-6xl px-5 py-20 sm:px-8">
        <div className="grid gap-6 lg:grid-cols-12">
          {s.offers.map((o, i) => <OfferCard key={o.id} offer={o} index={i} />)}
        </div>

        <Reveal className="mt-14">
          <div data-testid="disclaimer-block" className="glass flex items-start gap-5 rounded-3xl border-ember/25 p-7 sm:p-8">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-ember/30 bg-ember/10">
              <ShieldAlert className="h-5 w-5 text-ember" />
            </div>
            <div>
              <h3 className="font-serif text-xl text-white">{s.acc.disclaimerTitle}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{s.acc.disclaimer}</p>
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-14">
          <div className="flex flex-wrap items-center justify-between gap-6 rounded-3xl border border-mint/25 bg-mint/[0.06] p-8 sm:p-10">
            <div>
              <h3 className="font-serif text-2xl text-white sm:text-3xl">{s.acc.ctaTitle}</h3>
              <p className="mt-2 text-sm text-white/60">{s.acc.ctaText}</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <BookingTrigger testid="booking-open-button" className="btn-gold">
                <CalendarDays className="h-4 w-4" /> {s.acc.book}
              </BookingTrigger>
              <a href={waLink(s.wa.servicesMsg)} target="_blank" rel="noopener noreferrer" data-testid="offers-whatsapp-cta" className="btn-mint">
                <MessageCircle className="h-4 w-4" /> {s.acc.whatsapp}
              </a>
              <Link to="/contact" data-testid="offers-contact-cta" className="btn-gold">
                {s.acc.contact} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default Accompagnements;
